import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Zusatzposten einer Honorarnote (Okt 2026): Kammerbeiträge 20 %, Barauslagen 20 %, Barauslagen 0 %.
export const EXTRA_FIELDS = [
  ["kammer_20", "Rechtsanwaltskammerbeiträge", "20 % USt"],
  ["auslagen_20", "Barauslagen", "20 % USt"],
  ["auslagen_0", "Barauslagen", "0 % · durchlaufender Posten"],
];

export const emptyExtras = (init = {}) => Object.fromEntries(EXTRA_FIELDS.map(([k]) => [k, init[k] ?? ""]));

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const extrasPayload = (extras) => Object.fromEntries(EXTRA_FIELDS.map(([k]) => [k, num(extras[k])]));

export const grossWithExtras = (baseNet, extras) => {
  const e = extrasPayload(extras);
  return Math.round(((num(baseNet) + e.kammer_20 + e.auslagen_20) * 1.2 + e.auslagen_0) * 100) / 100;
};

export const LexExtrasFields = ({ value, onChange, testPrefix = "lex-extras" }) => (
  <div className="space-y-2" data-testid={`${testPrefix}-fields`}>
    <p className="text-xs uppercase tracking-widest font-semibold text-slate-400">Weitere Posten (optional, netto)</p>
    {EXTRA_FIELDS.map(([key, label, hint]) => (
      <div key={key} className="flex items-center justify-between gap-3">
        <span className="text-sm text-slate-700">
          {label} <span className="text-[11px] text-slate-400">· {hint}</span>
        </span>
        <input type="number" min="0" step="0.01" placeholder="0,00" value={value[key]}
          onChange={(e) => onChange({ ...value, [key]: e.target.value })}
          className="w-28 border border-slate-200 px-2 py-1.5 text-sm lex-mono text-right focus:outline-none focus:border-[#C5A059]"
          data-testid={`${testPrefix}-${key}`} />
      </div>
    ))}
  </div>
);

const fmtEur = (v) => new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(v || 0);

// In-App-Versand eines Entwurfs: vorher Zusatzposten abfragen.
export const LexSendDialog = ({ invoice, defaults = {}, busy, onClose, onSend }) => {
  const items = invoice.items || [];
  const [extras, setExtras] = useState(() => {
    const current = Object.fromEntries(items.filter((i) => i.lex_extra_key).map((i) => [i.lex_extra_key, i.total]));
    const useTemplate = !Object.keys(current).length && !invoice.lex_extra;
    return emptyExtras(useTemplate ? Object.fromEntries(Object.entries(defaults).filter(([, v]) => v)) : current);
  });
  const baseNet = items.length
    ? items.filter((i) => !i.lex_extra_key).reduce((s, i) => s + Number(i.total || 0), 0)
    : (invoice.subtotal ?? 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="lex-theme max-w-md" data-testid="lex-send-dialog">
        <DialogHeader>
          <DialogTitle className="lex-serif text-2xl" style={{ color: "#0A1128" }}>Note an Kanzlei senden</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Note <span className="lex-mono">{invoice.invoice_number}</span> · Kommen noch Kammerbeiträge oder Barauslagen dazu?
          </DialogDescription>
        </DialogHeader>
        <LexExtrasFields value={extras} onChange={setExtras} testPrefix="lex-send-extras" />
        <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-sm">
          <span className="text-slate-500">Gesamt brutto</span>
          <span className="lex-mono font-semibold" data-testid="lex-send-gross">{fmtEur(grossWithExtras(baseNet, extras))}</span>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-widest text-slate-500 px-4 py-2"
            data-testid="lex-send-cancel">Abbrechen</button>
          <button type="button" onClick={() => onSend(extrasPayload(extras))} disabled={busy} className="lex-btn !px-5 disabled:opacity-50"
            data-testid="lex-send-submit">{busy ? "Sendet …" : "An Kanzlei senden"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
